import React from "react";
import Switch from "react-switch";
import CleaveInput from "../Components/CleaveInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import OnlineWrapper from "./OnlineWrapper";
import OfflineWrapper from "./OfflineWrapper";

const VENUE = "Venue";

const SettlementCalculator = ({
  event,
  setEvent,
  total_sales,
  event_fees_total,
  before_hall_event_fees_total,
  clothingSetup,
  otherSetup,
  musicSetup,
  setShowButtons,
  recalculateAlert,
  setRecalculateAlert,
  artistPaymentMatchAlert,
  venuePaymentMatchAlert,
  setDataCached,
  hideButtons,
  changesMade,
}) => {
  const handleChange = (e, offlineState) => {
    changesMade();
    setDataCached(offlineState);
    setEvent({
      ...event,
      [e.target.id]: e.target.value,
    });
  };

  const calculateArtist = (offlineState) => {
    const cc_net = clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee;
    const cc_fee_kept_by_venue = event.vendor === VENUE ? cc_net : 0.0;
    const rate = parseFloat(event.currency_exchange_rate) || 0;
    const effectiveRate = rate > 0 ? rate : 1;

    // Apply exchange rate to computed monetary fields only
    const applyRate = (updates) => {
      if (effectiveRate === 1) return updates;
      const fields = ['gross_sales', 'taxable_gross', 'tax_net', 'hall_net', 'cc_net',
        'fee_net', 'total_from_venue', 'total_to_artist', 'total_to_venue', 'cc_fee_kept_by_venue'];
      const result = { ...updates };
      fields.forEach((f) => {
        if (typeof result[f] === 'number') {
          result[f] = result[f] * effectiveRate;
        }
      });
      return result;
    };

    setRecalculateAlert(false);
    setDataCached(offlineState);
    setShowButtons(true);

    if (event.manual_calc) {
      const grossUsd = total_sales * effectiveRate;
      const hallUsd = parseFloat(event.hall_net.toString().replace(/,/g, "")) * effectiveRate;
      const ccUsd = parseFloat(event.cc_net.toString().replace(/,/g, "")) * effectiveRate;
      const taxUsd = parseFloat(event.tax_net.toString().replace(/,/g, "")) * effectiveRate;
      const ccFeeKeptUsd = cc_fee_kept_by_venue * effectiveRate;
      const beforeHallFeesUsd = before_hall_event_fees_total * effectiveRate;
      const eventFeesUsd = event_fees_total * effectiveRate;

      if (event.tax_kept_by_venue) {
        setEvent({
          ...event,
          gross_sales: grossUsd,
          hall_net: hallUsd,
          cc_net: ccUsd,
          tax_net: taxUsd,
          taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
          total_from_venue: grossUsd - hallUsd - ccUsd - taxUsd,
          total_to_artist: grossUsd - hallUsd - ccUsd - taxUsd + beforeHallFeesUsd - eventFeesUsd,
          total_to_venue: hallUsd + taxUsd + ccFeeKeptUsd,
        });
        return;
      }

      if (event.remitted) {
        setEvent({
          ...event,
          gross_sales: grossUsd,
          hall_net: hallUsd,
          cc_net: ccUsd,
          tax_net: taxUsd,
          taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
          total_from_venue: grossUsd - hallUsd - ccUsd,
          total_to_artist: grossUsd - hallUsd - ccUsd - taxUsd + beforeHallFeesUsd - eventFeesUsd,
          total_to_venue: grossUsd - ccFeeKeptUsd,
        });
        return;
      }

      setEvent({
        ...event,
        gross_sales: grossUsd,
        hall_net: hallUsd,
        cc_net: ccUsd,
        tax_net: taxUsd,
        taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
        total_from_venue: grossUsd - hallUsd - ccUsd,
        total_to_artist: grossUsd - hallUsd - ccUsd + beforeHallFeesUsd - eventFeesUsd,
        total_to_venue: hallUsd + ccFeeKeptUsd,
      });
      return;
    }

    if (event.tax_kept_by_venue) {
      const updates = {
        gross_sales: total_sales,
        taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
        tax_net: clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax,
        hall_net: clothingSetup.gross_hall + otherSetup.gross_hall + musicSetup.gross_hall,
        cc_net,
        cc_fee_kept_by_venue,
        fee_net: event_fees_total,
        total_from_venue:
          total_sales -
          (clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax) -
          (clothingSetup.gross_hall + otherSetup.gross_hall + musicSetup.gross_hall) -
          (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee),
        total_to_artist:
          total_sales -
          (clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax) -
          (clothingSetup.gross_hall + otherSetup.gross_hall + musicSetup.gross_hall) -
          (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee) +
          before_hall_event_fees_total -
          event_fees_total,
        total_to_venue:
          parseFloat(clothingSetup.gross_hall) +
          parseFloat(otherSetup.gross_hall) +
          parseFloat(musicSetup.gross_hall) +
          (clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax) +
          cc_fee_kept_by_venue,
      };
      setEvent({ ...event, ...applyRate(updates) });
      return;
    }

    if (event.remitted) {
      const updates = {
        gross_sales: total_sales,
        taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
        tax_net: clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax,
        cc_net,
        cc_fee_kept_by_venue,
        hall_net: clothingSetup.gross_hall + otherSetup.gross_hall + musicSetup.gross_hall,
        fee_net: event_fees_total,
        total_from_venue:
          total_sales -
          (parseFloat(clothingSetup.gross_hall) +
            parseFloat(otherSetup.gross_hall) +
            parseFloat(musicSetup.gross_hall)) -
          (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee),
        total_to_artist:
          total_sales -
          (parseFloat(clothingSetup.gross_hall) +
            parseFloat(otherSetup.gross_hall) +
            parseFloat(musicSetup.gross_hall)) -
          (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee) +
          before_hall_event_fees_total -
          event_fees_total -
          clothingSetup.gross_tax -
          otherSetup.gross_tax -
          musicSetup.gross_tax,
        total_to_venue:
          parseFloat(clothingSetup.gross_hall) +
          parseFloat(otherSetup.gross_hall) +
          parseFloat(musicSetup.gross_hall) +
          cc_fee_kept_by_venue,
      };
      setEvent({ ...event, ...applyRate(updates) });
      return;
    }

    const updates = {
      gross_sales: total_sales,
      taxable_gross: clothingSetup.taxable + otherSetup.taxable + musicSetup.taxable,
      tax_net: clothingSetup.gross_tax + otherSetup.gross_tax + musicSetup.gross_tax,
      hall_net: clothingSetup.gross_hall + otherSetup.gross_hall + musicSetup.gross_hall,
      cc_net,
      cc_fee_kept_by_venue,
      fee_net: event_fees_total,
      total_from_venue:
        total_sales -
        (parseFloat(clothingSetup.gross_hall) + parseFloat(otherSetup.gross_hall) + parseFloat(musicSetup.gross_hall)) -
        (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee),
      total_to_artist:
        total_sales -
        (parseFloat(clothingSetup.gross_hall) + parseFloat(otherSetup.gross_hall) + parseFloat(musicSetup.gross_hall)) -
        (clothingSetup.cc_fee + otherSetup.cc_fee + musicSetup.cc_fee) +
        before_hall_event_fees_total -
        event_fees_total,
      total_to_venue:
        parseFloat(clothingSetup.gross_hall) +
        parseFloat(otherSetup.gross_hall) +
        parseFloat(musicSetup.gross_hall) +
        cc_fee_kept_by_venue,
    };
    setEvent({ ...event, ...applyRate(updates) });
  };

  const handleToggle = (state, offlineState) => {
    setDataCached(offlineState);
    changesMade();
    setEvent({
      ...event,
      manual_calc: !state,
    });
  };

  const renderForm = (offlineState) => {
    return (
      <>
        {!hideButtons ? (
          <div className="bg-gray-50 pb-2 px-8 border text-black text-sm">
            <div className="mt-6 text-lg font-medium">Artist</div>
            <hr className="mt-2 mb-3"></hr>
            <div>
              <div className="flex justify-between">
                <div className="flex self-center">
                  <Switch
                    onChange={() => {
                      handleToggle(event.manual_calc, offlineState);
                    }}
                    checked={!!event.manual_calc}
                    height={22}
                    width={40}
                    onColor="#3b82f6"
                    offColor="#d4d4d8"
                    id="manual_calc"
                  />
                  <label className="ml-2 text-xs font-light self-center" htmlFor="manual_calc">
                    Calculate manually
                  </label>
                </div>
                <div>
                  <button
                    className="h-8 bg-blue-500 text-white text-xs py-2 px-3 rounded-md hover:bg-blue-700"
                    onClick={() => calculateArtist(offlineState)}
                  >
                    Calculate
                  </button>
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Gross Sales</div>
                <div className="w-1/2">
                  <CleaveInput
                    id="gross_sales"
                    value={
                      event.gross_sales
                        ? event.gross_sales?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="gross_sales"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    disabled
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Taxable Gross</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled
                    id="taxable_gross"
                    value={
                      event.taxable_gross
                        ? event.taxable_gross?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="taxable_gross"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Net Tax Paid</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="tax_net"
                    value={event.tax_net?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                    name="tax_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Net Hall Fee</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="hall_net"
                    value={
                      event.hall_net
                        ? event.hall_net?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="hall_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Net CC Fee</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="cc_net"
                    value={
                      event.cc_net
                        ? event.cc_net?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="cc_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Currency Exchange Rate</div>
                <div className="w-1/2">
                  <CleaveInput
                    id="currency_exchange_rate"
                    value={
                      event.currency_exchange_rate
                        ? event.currency_exchange_rate?.toString()
                        : ""
                    }
                    name="currency_exchange_rate"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                      numeralDecimalScale: 5,
                    }}
                    inputClassName="w-full"
                    placeholder="e.g. 0.72"
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Net Event Fee</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="fee_net"
                    value={
                      event.fee_net
                        ? event.fee_net?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="fee_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Total from Venue</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled
                    id="total_from_venue"
                    value={event.total_from_venue?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                    name="total_from_venue"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Total to Artist</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled
                    id="total_to_artist"
                    value={event.total_to_artist?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                    name="total_to_artist"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="mt-10 text-lg font-medium">Venue</div>
              <hr className="mt-2 mb-3"></hr>
              <div className="flex justify-between my-4">
                <div className="self-center">Net Hall Fee</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="hall_net"
                    value={
                      event.hall_net
                        ? event.hall_net?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="hall_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">Net Tax Paid</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="tax_net"
                    value={
                      event.tax_kept_by_venue
                        ? event.tax_net?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="tax_net"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div className="flex justify-between my-4">
                <div className="self-center">CC Fee Kept By Venue</div>
                <div className="w-1/2">
                  <CleaveInput
                    disabled={!event.manual_calc}
                    id="cc_fee_kept_by_venue"
                    numericResult
                    value={
                      event.vendor === VENUE
                        ? event.cc_fee_kept_by_venue?.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })
                        : ""
                    }
                    name="cc_fee_kept_by_venue"
                    onChange={(e) => handleChange(e, offlineState)}
                    onClick={(e) => e.target.select()}
                    cleaveOptions={{
                      numeral: true,
                      numeralPositiveOnly: true,
                    }}
                    inputClassName="w-full"
                    prefix={"$"}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between my-4">
                  <div className="self-center">Total to Venue</div>
                  <div className="w-1/2">
                    <CleaveInput
                      disabled
                      id="total_to_venue"
                      value={event.total_to_venue?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                      name="total_to_venue"
                      onChange={(e) => handleChange(e, offlineState)}
                      onClick={(e) => e.target.select()}
                      cleaveOptions={{
                        numeral: true,
                        numeralPositiveOnly: true,
                      }}
                      inputClassName="w-full"
                      prefix={"$"}
                    />
                  </div>
                </div>
                {recalculateAlert && (
                  <div className="text-red-500 m-1">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Changes have been made to some variables that may affect the venue settlement calculation. Click
                    "Calculate" above to see the updated outputs.
                  </div>
                )}
                {artistPaymentMatchAlert && (
                  <div className="text-red-500 m-1">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Artists payment info does not match settlement info.
                  </div>
                )}
                {venuePaymentMatchAlert && (
                  <div className="text-red-500 m-1">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Venue payment info does not match settlement info.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-2 w-11/12 border text-black text-sm">
            <div className="mt-10 text-lg font-medium">Artist</div>
            <hr></hr>
            <div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Gross Sales</div>
                <div className="w-1/3 text-right">
                  {event.gross_sales?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Taxable Gross</div>
                <div className="w-1/3 text-right">
                  {event.taxable_gross?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Net Tax Paid</div>
                <div className="w-1/3 text-right">
                  {event.tax_net?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Net Hall Fee</div>
                <div className="w-1/3 text-right">
                  {event.hall_net?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Net CC Fee</div>
                <div className="w-1/3 text-right">
                  {event.cc_net?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              {parseFloat(event.currency_exchange_rate) > 0 && (
                <div className="flex justify-between my-2">
                  <div className="self-center font-semibold">Currency Exchange Rate</div>
                  <div className="w-1/3 text-right">
                    {event.currency_exchange_rate}
                  </div>
                </div>
              )}
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Net Event Fee</div>
                <div className="w-1/3 text-right">
                  {event.fee_net?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="flex justify-between my-2">
                <div className="self-center font-semibold">Total from Venue</div>
                <div className="w-1/3 text-right">
                  {event.total_from_venue?.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div className="mt-2 text-lg font-medium">Venue</div>
              <hr></hr>
              <div>
                <div className="flex justify-between my-2">
                  <div className="self-center font-semibold">Net Hall Fee</div>
                  <div className="w-1/3 text-right">
                    {event.hall_net?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                </div>
                <div className="flex justify-between my-2">
                  <div className="self-center font-semibold">Net Tax Paid</div>
                  <div className="w-1/3 text-right">
                    {event.tax_kept_by_venue
                      ? event.tax_net?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      : ""}
                  </div>
                </div>
                <div className="flex justify-between my-2">
                  <div className="self-center font-semibold">CC Fee Kept By Venue</div>
                  <div className="w-1/3 text-right">
                    {(event.cc_fee_kept_by_venue || 0).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                </div>
                <div className="flex justify-between my-2">
                  <div className="self-center font-semibold">Total to Venue</div>
                  <div className="w-1/3 text-right">
                    {event.total_to_venue?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="w-3/8 h-screen -mt-40 -mr-12">
      <div className="-mt-1">
        <header className="py-8 font-medium px-8 text-2xl border bg-blue-500 text-white">
          <div>Event Settlements</div>
        </header>
        <div>
          <OnlineWrapper>{renderForm(false)}</OnlineWrapper>
          <OfflineWrapper>{renderForm(true)}</OfflineWrapper>
        </div>
      </div>
    </div>
  );
};

export default SettlementCalculator;
